// ─── A saved bill's tax, per rate (gst-parked task 080) ──────────────────────
//
// Receipts and invoices rebuild the per-rate breakup from the bill's lines.
// They used to assume CGST + SGST and put the odd paisa on SGST; the bill now
// carries the tax type it was saved with (`supply_type`), and the split
// follows the server's rule (services/api/src/billing/tax-presentation.ts):
// per line SGST = floor(tax / 2), CGST takes the odd paisa. IGST (INTER)
// keeps the whole line tax. No `supply_type` (a VAT bill, an older API) is
// treated as same-state, the resolver's default (owner decision D4).

import type { BillSupplyType } from '@bbpos/types';

/**
 * gst-parked 090 (D5): what a bill of a business that is not GST registered
 * prints instead of a GSTIN and a tax breakup (mirror of the API's
 * `NOT_GST_REGISTERED`, services/api/src/billing/tax-presentation.ts).
 */
export const NOT_GST_REGISTERED = 'Not GST registered';

export interface BillTaxSlab {
  rate: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  items: { name: string; amount: number }[];
}

export function billTaxSlabs(
  items: ReadonlyArray<{
    gst_rate: number;
    line_subtotal: number;
    line_tax: number;
    line_total: number;
    product_name: string;
  }>,
  supplyType: BillSupplyType | null | undefined,
): BillTaxSlab[] {
  const inter = supplyType === 'INTER';
  const byRate = new Map<number, BillTaxSlab>();
  for (const item of items) {
    const rate = Number(item.gst_rate);
    const lineTax = Number(item.line_tax);
    const slab = byRate.get(rate) ?? { rate, taxableAmount: 0, cgst: 0, sgst: 0, igst: 0, items: [] };
    slab.taxableAmount += Number(item.line_subtotal);
    if (inter) {
      slab.igst += lineTax;
    } else {
      const sgst = Math.floor(lineTax / 2);
      slab.sgst += sgst;
      slab.cgst += lineTax - sgst;
    }
    slab.items.push({ name: item.product_name, amount: Number(item.line_total) });
    byRate.set(rate, slab);
  }
  return Array.from(byRate.values());
}
